export function useStructuredData(opts = {}) {
	const config = useAppConfig();
	const {getInfo} = useInfo();
	const {getCurrency} = useCurrency();
	const {stripHtml, formatDate, limitWords} = useText();
	const {getAppUrl} = useApiRoutes({route: opts.route});
	const labels = useLabels();

	// generate schema.org structured data if enabled
	function generateStructuredData(options) {
		if (config.structuredData?.enabled === false) return '';
		let schema = '';

		// generate schema for homepage
		if (options.controller == 'cms' && options.template == 'CmsHomepage') {
			schema = basicData();
		}

		// generate schema for publish category page
		if (options.controller == 'publish' && options.action == 'index' && options.contentType == 'category') {
			schema = publishCategoryData(options.data);
		}

		// generate schema for publish detail page
		if (options.controller == 'publish' && options.action == 'detail') {
			schema = publishDetailData(options.data);
		}

		// generate schema for catalog category page
		if (options.controller == 'catalog' && options.action == 'index' && ['category', 'list', 'manufacturer'].includes(options.contentType)) {
			schema = catalogCategoryData(options.data);
		}

		// generate schema for product page
		if (options.controller == 'catalog' && options.action == 'detail') {
			schema = catalogDetailData(options.data);
		}

		return schema;
	}

	function parseOrganizationData() {
		const label = labels.get('organization_structured_data');
		if (!label) return null;

		// Parse each "key: value;" line into a key-value object
		const result = {};
		label.split('\n').forEach(line => {
			const colonIndex = line.indexOf(':');
			if (colonIndex !== -1) {
				const key = line.substring(0, colonIndex).trim();
				const value = line
					.substring(colonIndex + 1)
					.trim()
					.replace(/;$/, '')
					.trim();
				if (key && value) result[key] = value;
			}
		});

		return Object.keys(result).length ? result : null;
	}

	function basicData() {
		const parsed = parseOrganizationData();
		const catalogUrl = getAppUrl('catalog', {log: false});
		const searchAction = catalogUrl ? `,"potentialAction": {"@type": "SearchAction","target": {"@type": "EntryPoint","urlTemplate": "${getInfo('site_url')}${catalogUrl}?search_q={search_term_string}"},"query-input": "required name=search_term_string"}` : '';

		return `{
			"@context": "https://schema.org",
			"@graph": [
				{
					"@type": "WebSite",
					"@id": "${getInfo('site_url')}#website",
					"url": "${getInfo('site_url')}",
					"name": "${parsed?.organizationName || config.structuredData?.basic?.name}",
					"publisher": {
						"@id": "${getInfo('site_url')}#organization"
					}
					${searchAction}
				},
				${organizationData()}
			]
		}`;
	}

	function organizationData() {
		const parsed = parseOrganizationData();
		const b = config.structuredData?.basic ?? {};
		const siteUrl = getInfo('site_url');

		// Get value: prefer parsed label data, fallback to app.config
		// If label value is 'null', explicitly skip the field (return null, no fallback)
		const get = (key, fallback) => {
			if (parsed?.[key] === 'null') return null;
			return parsed?.[key] !== undefined && parsed[key] !== '' ? parsed[key] : fallback;
		};

		const organizationName = get('organizationName', b.organizationName);
		const logo = get('logo', b.logo);
		const vatID = get('vatID', b.vatID);
		const taxID = get('taxID', b.taxID);
		const foundingDate = get('foundingDate', b.foundingDate);
		const brandVal = get('brand', b.brand);
		const phone = get('telephone', b.phone);
		const email = get('email', b.email);
		// Format social URLs from label
		// If label value is 'null', explicitly skip
		const socialNetworkUrls =
			parsed?.social === 'null'
				? null
				: parsed?.social
					? parsed.social
							.split(',')
							.map(u => `"${u.trim()}"`)
							.join(',')
					: b.socialNetworkUrls;

		// Address fields (parsed keys are flat, config keys are nested under address)
		const streetAddress = get('streetAddress', b.address?.streetAddress);
		const addressLocality = get('addressLocality', b.address?.addressLocality);
		const addressRegion = get('addressRegion', b.address?.addressRegion);
		const postalCode = get('postalCode', b.address?.postalCode);
		const addressCountry = get('addressCountry', b.address?.addressCountry);
		const hasAddress = streetAddress || addressLocality || addressRegion || postalCode || addressCountry;

		const address = hasAddress
			? `,"address": {
				"@type": "PostalAddress"
				${streetAddress ? ',"streetAddress": "' + streetAddress + '"' : ''}
				${addressLocality ? ',"addressLocality": "' + addressLocality + '"' : ''}
				${addressRegion ? ',"addressRegion": "' + addressRegion + '"' : ''}
				${postalCode ? ',"postalCode": "' + postalCode + '"' : ''}
				${addressCountry ? ',"addressCountry": "' + addressCountry + '"' : ''}
			}`
			: '';

		const brand = brandVal ? `,"brand": {"@type": "Brand","name": "${brandVal}"}` : '';
		// Parse CSV label values into arrays, fallback to config arrays
		// If label value is 'null', explicitly skip the field
		const knowsAboutArr =
			parsed?.knowsAbout === 'null'
				? null
				: parsed?.knowsAbout
					? parsed.knowsAbout
							.split(',')
							.map(v => v.trim())
							.filter(Boolean)
					: b.knowsAbout;
		const makesOfferArr =
			parsed?.makesOffer === 'null'
				? null
				: parsed?.makesOffer
					? parsed.makesOffer
							.split(',')
							.map(v => v.trim())
							.filter(Boolean)
					: b.makesOffer;
		const areaServedArr =
			parsed?.areaServed === 'null'
				? null
				: parsed?.areaServed
					? parsed.areaServed
							.split(',')
							.map(v => v.trim())
							.filter(Boolean)
					: b.areaServed;
		const knowsAbout = knowsAboutArr?.length ? `,"knowsAbout": [${knowsAboutArr.map(k => `"${k.replace(/"/g, '\\"')}"`).join(',')}]` : '';
		const areaServed = areaServedArr?.length ? `,"areaServed": [${areaServedArr.map(a => `"${a}"`).join(',')}]` : '';
		const makesOffer = makesOfferArr?.length ? `,"makesOffer": [${makesOfferArr.map(o => `{"@type": "Offer","itemOffered": {"@type": "Service","name": "${o.replace(/"/g, '\\"')}"}}`).join(',')}]` : '';

		// Build optional fields as array and join to avoid empty lines
		const optionalFields = [
			logo ? `,"logo": "${logo}"` : '',
			vatID ? `,"vatID": "${vatID}"` : '',
			taxID ? `,"taxID": "${taxID}"` : '',
			foundingDate ? `,"foundingDate": "${foundingDate}"` : '',
			brand,
			address,
			`,"contactPoint": {
					"@type": "ContactPoint",
					"contactType": "Customer Service"` +
				(phone ? `,"telephone": "${phone}"` : '') +
				(email ? `,"email": "${email}"` : '') +
				`}`,
			socialNetworkUrls ? `,"sameAs": [${socialNetworkUrls}]` : '',
			knowsAbout,
			areaServed,
			makesOffer,
		]
			.filter(Boolean)
			.join('\n\t\t\t\t');

		return `{
				"@type": "Organization",
				"@id": "${siteUrl}#organization",
				"name": "${organizationName}",
				"url": "${siteUrl}"
				${optionalFields}
			}`;
	}

	function publishCategoryData(data) {
		const bc = breadcrumbs(data);

		return `{
			"@context": "https://schema.org",
			"@graph": [
				{
					"@type": "CollectionPage",
					"name": "${data.title?.replace(/"/g, '\\"')}"
				},
				{
					"@type": "BreadcrumbList",
					"itemListElement": [${bc}]
				}
			]
		}`;
	}

	function breadcrumbs(data) {
		let bc = '';
		if (data?.breadcrumbs?.length) {
			const totalBreadcrumbs = data.breadcrumbs.length;
			data.breadcrumbs.forEach((el, index) => {
				bc += `
					{
						"@type": "ListItem",
						"position": ${index + 1},
						"item": {
							"@id": "${el.url}",
							"name": "${el.title?.replace(/"/g, '\\"')}"
						}
					}${index < totalBreadcrumbs - 1 ? ',' : ''}
				`;
			});
		}

		return bc;
	}

	function publishDetailData(data) {
		const bc = breadcrumbs(data);
		const parsed = parseOrganizationData();
		const organizationName = (parsed?.organizationName !== 'null' && parsed?.organizationName) || config.structuredData?.basic?.organizationName;
		const organizationLogo = (parsed?.logo !== 'null' && parsed?.logo) || config.structuredData?.basic?.logo;
		const datePublished = data.datetime_published ? formatDate(data.datetime_published, 'YYYY-MM-DDTHH:mm:ss') : '';
		let dateModified = data.datetime_updated ? formatDate(data.datetime_updated, 'YYYY-MM-DDTHH:mm:ss') : '';
		if (!dateModified) dateModified = datePublished;

		const image = () => {
			if (data.main_image_upload_path) {
				return `"image": {
					"@type": "ImageObject",
					"url": "${getInfo('site_url')}${data.main_image_upload_path}"
				}`;
			}
			return '';
		};

		const comments = () => {
			if (data.feedback_comment_widget?.items?.length) {
				return `,"commentCount": "${data.feedback_comment_widget.items.length}"
					,"comment": [
					${data.feedback_comment_widget.items
						.map(
							el => `{
						"@type": "Comment",
						"author": {
							"@type": "Person",
							"name": "${el.display_name}"
						},
						${el.message ? '"text": "' + stripHtml(el.message).replace(/"/g, '\\"') + '",' : ''}
						"dateCreated": "${formatDate(el.datetime_created, 'YYYY-MM-DDTHH:mm:ss')}"
					}`,
						)
						.join(',')}
				]`;
			}
			return '';
		};

		return `{
			"@context": "https://schema.org",
			"@graph": [
				{
					"@type": "${['recipes'].includes(data.category_template) ? 'Recipe' : 'NewsArticle'}",
					"${['recipes'].includes(data.category_template) ? 'name' : 'headline'}": "${data.seo_title?.replace(/"/g, '\\"')}",
					"description": "${description(data)}",
					"author": {
						"@type": "Organization",
						"name": "${organizationName}"
					},
					"publisher": {
						"@type": "Organization",
						"name": "${organizationName}",
						"logo": {
							${organizationLogo ? '"@type": "ImageObject",' : ''}
							${organizationLogo ? '"url": "' + organizationLogo + '"' : ''}
						}
					},
					"datePublished": "${datePublished}",
					"dateModified": "${dateModified}"
					${['recipes'].includes(data.category_template) ? rating(data) : ''}
					${['recipes'].includes(data.category_template) ? reviews(data) : ''}
					${['recipes'].includes(data.category_template) ? recipeDetails(data) : ''}
					${['recipes'].includes(data.category_template) ? '' : comments()}
					,"mainEntityOfPage": {
						"@type": "WebPage",
						"@id": "${data.url}"
					},
					${image()}
				},
				{
					"@type": "BreadcrumbList",
					"itemListElement": [${bc}]
				}
			]
		}`;
	}

	function catalogCategoryData(data) {
		const bc = breadcrumbs(data);

		return `{
			"@context": "https://schema.org",
			"@graph": [
				{
					"@type": "CollectionPage",
					"name": "${data.title?.replace(/"/g, '\\"')}"
				},
				{
					"@type": "BreadcrumbList",
					"itemListElement": [${bc}]
				}
			]
		}`;
	}

	function catalogDetailData(data) {
		const bc = breadcrumbs(data);
		const parsed = parseOrganizationData();
		const organizationName = (parsed?.organizationName !== 'null' && parsed?.organizationName) || config.structuredData?.basic?.organizationName;
		const image = () => {
			if (data.main_image_upload_path) {
				return `,"image": "${getInfo('site_url')}${data.main_image_upload_path}"`;
			}
			return '';
		};

		const offerShippingDetails = () => {
			if (config.structuredData?.basic?.shippingCountries?.length) {
				const countries = config.structuredData.basic.shippingCountries;
				return `,"shippingDetails": {
					"@type": "OfferShippingDetails",
					"shippingDestination": [
						${countries
							.map(
								country => `{
							"@type": "DefinedRegion",
							"addressCountry": "${country}"
						}`,
							)
							.join(',')}
					]
				}`;
			}
			return '';
		};

		const offerReturnPolicy = () => {
			if (config.structuredData?.basic?.returnDays && config.structuredData?.basic?.shippingCountries?.length) {
				return `,"hasMerchantReturnPolicy": {
					"@type": "MerchantReturnPolicy",
					"returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
					"merchantReturnDays": ${config.structuredData.basic.returnDays},
					"applicableCountry": ${JSON.stringify(config.structuredData.basic.shippingCountries)}
				}`;
			}
			return '';
		};

		return `{
			"@context": "https://schema.org",
			"@graph": [
				{
					"@type": "Product",
					"name": "${data.title?.replace(/"/g, '\\"')}",
					"sku": "${data.code}",
					"mpn": "${data.id}"
					${image()},
					"description": "${description(data)}"
					${brand(data)}
					${rating(data)}
					${reviews(data)}
					,"offers": {
						"@type": "Offer",
						"url": "${data.url}",
						"priceCurrency": "${getCurrency('code')}",
						${price(data)},
						"priceValidUntil": "${new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}",
						${availability(data)},
						"itemCondition": "https://schema.org/NewCondition",
						"seller": {
							"@type": "Organization",
							"name": "${organizationName}"
						}
						${offerShippingDetails()}
						${offerReturnPolicy()}
					}
				},
				{
					"@type": "BreadcrumbList",
					"itemListElement": [${bc}]
				}
			]
		}`;
	}

	function description(data) {
		let description = '';
		if (data.seo_description) {
			description = data.seo_description;
		} else if (data.short_description) {
			description = data.short_description;
		} else if (data.content) {
			description = data.content;
		}

		description = description.replace(/"/g, '\\"'); // escape double quotes
		return limitWords(stripHtml(description), 50);
	}

	function brand(data) {
		if (data.manufacturer_title) {
			return `,"brand": {
				"@type": "Brand",
				"name": "${data.manufacturer_title}"
			}`;
		}

		return '';
	}

	function rating(data) {
		if (data.feedback_rate_widget && Number(data.feedback_rate_widget?.rates_votes)) {
			return `,"aggregateRating": {
					"@type": "AggregateRating",
					"ratingValue": "${Number(data.feedback_rate_widget.rates).toFixed(1)}",
					"reviewCount": "${data.feedback_rate_widget.rates_votes}"
				}`;
		}

		return '';
	}

	function reviews(data) {
		if (data.feedback_comment_widget?.items?.length) {
			let reviews = '';
			const items = Object.values(data.feedback_comment_widget.items);
			const totalReviews = items.length;
			items.forEach((el, index) => {
				let msg = el.message;
				msg = msg.replace(/"/g, '\\"'); // escape double quotes
				reviews += `{
						"@type": "Review",
						"author": {
							"@type": "Person",
							"name": "${el.display_name}"
						}
						${el.message ? ',"reviewBody": "' + stripHtml(msg) + '"' : ''}
						${
							el.rate
								? `,"reviewRating": {
							"@type": "Rating",
							"ratingValue": "${el.rate}"
						}`
								: ''
						}
					}${index < totalReviews - 1 ? ',' : ''}`;
			});

			return `,"review": [${reviews}]`;
		}

		return '';
	}

	function recipeDetails(data) {
		// Get cook time in minutes from hook and convert to ISO 8601 duration format
		let cookTime = '';
		if (config.structuredData?.hooks?.recipeCookTime) {
			const minutes = config.structuredData.hooks.recipeCookTime(data);
			if (minutes) cookTime = `PT${minutes}M`;
		}

		// Get ingredients from hook and format as array
		let ingredients = '';
		if (config.structuredData?.hooks?.recipeIngredient) {
			const ingredientString = config.structuredData.hooks.recipeIngredient(data);
			if (ingredientString) {
				// Split by paragraph tags or br tags
				const ingredientArray = ingredientString
					.split(/<\/p>|<br\s*\/?>|<p>/)
					.map(item => stripHtml(item).trim())
					.filter(item => item.length > 0)
					.map(item => `"${item.replace(/"/g, '\\"')}"`);

				if (ingredientArray.length > 0) {
					ingredients = ingredientArray.join(',');
				}
			}
		}

		let category = '';
		if (config.structuredData?.hooks?.recipeCategory) {
			category = config.structuredData.hooks.recipeCategory(data);
		}

		// If no data, return empty string
		if (!cookTime && !ingredients && !category) return '';

		return `
			${cookTime ? `,"cookTime": "${cookTime}"` : ''}
			${ingredients ? `,"recipeIngredient": [${ingredients}]` : ''}
			${category ? `,"recipeCategory": "${category}"` : ''}
			${data.seo_keywords ? `,"keywords": "${data.seo_keywords.replace(/"/g, '\\"')}"` : ''}
		`;
	}

	function availability(data) {
		return data?.is_available ? '"availability": "https://schema.org/InStock"' : '"availability": "https://schema.org/OutOfStock"';
	}

	function price(data) {
		return `"price": "${data.price_custom}"`;
	}

	return {generateStructuredData};
}
